import {Assert, Clone, E, Timer} from "js-vextensions";
import {default as moment} from "moment";
import {AssertNotify, RunInAction, weekInMS} from "web-vcore";
import {AddSession} from "../Server/Commands/SessionCommands.js";
import {AutoEndType} from "../Server/DB/engineConfigs/@EngineConfig.js";
import {UpdateLocalSessionsMap} from "../Server/DB/sessions.js";
import {Session} from "../Server/DB/sessions/@Session.js";
import {TimelineEvent} from "../Server/DB/timelineEvents/@TimelineEvent.js";
import {LogType} from "../UI/Tools/@Shared/LogEntry.js";
import {InDesktop} from "../Utils/Bridge/Bridge_Native.js";
import {DeleteSessionFolder, SaveSessionGeneralData} from "../Utils/Bridge/Bridge_Preload.js";
import {gdBelt_rawDataDB} from "../Utils/IndexedDB/GDBelt_RawDataDB.js";
import {LiveSession} from "./LiveSession.js";
import {SessionEvent} from "./LiveSession/SessionEvent.js";
import {ClearAndStopLiveSession, liveSession} from "./LiveSessionManagement.js";

/*//const ORM_EncoderWorker = require("worker-loader!opus-media-recorder/encoderWorker.js");
const ORM_EncoderWorker = require("worker-loader!opus-media-recorder/encoderWorker.umd.js"); // umd version needed for webpack 5
/*const OMR_OggOpusWasm = require("opus-media-recorder/OggOpusEncoder.wasm");
//const OMR_WebMOpusWasm = require("opus-media-recorder/WebMOpusEncoder.wasm");*/
// async wrapper
/*let OMR_OggOpusWasm;
void (async function() {
	OMR_OggOpusWasm = await import("opus-media-recorder/OggOpusEncoder.wasm");
}());*#/*/

export type MicLoudnessListener = (notifyLoudness: number, actualLoudness: number) => any;

// the desktop bridge merely passed on the message from a remote android instance
/*nativeBridge.RegisterFunction("Remote_SnoozeFail", ()=>{
	if (liveSession && liveSession.IsLocal()) {
		console.log("Got snooze call from remote, android!");
		liveSession.Snooze("Snooze (remote, android)");
		return "Applied.";
	}
});*/

export function ChangeTimestampDateToBeAfterNow(timestamp: number) {
	const yesterdayDateStr = moment().subtract(1, "day").format("YYYY-MM-DD");
	const timeOfDayStr = moment(timestamp).format("HH:mm:ss");
	let modifiedTimestamp = moment(`${yesterdayDateStr} ${timeOfDayStr}`);

	// keep increasing the time by 1 day, until it is past the current time (should generally only take 1 or 2 iterations)
	while (modifiedTimestamp.valueOf() < Date.now()) {
		modifiedTimestamp = modifiedTimestamp.add(1, "day");
	}

	return modifiedTimestamp.valueOf();
}

export class LiveSession_Local extends LiveSession {
	/*constructor(config: EngineConfig) {
		super(config);
		this.snoozeAndPrompts.memoryPrompt.s = this;
	}*/

	/** Packages some data together, to be stored in a TimelineEvent (or sent to an evaluator). */
	GetSessionInfo(includeLog = false): Session {
		AssertNotify(this.coreData.id_live != null, "Live-session's id should not be null."); // hit
		const result = new Session(E(
			{
				id: this.coreData.id_live, // temporarily assign the id the session had while it was live (this ID will be kept actually, IF the session is being saved offline only; see FinalizeStoredData func)
				//id: "<placeholder; server will assign final id>",
				creator: this.userID,
				configID: this.c.id,
				config_fieldOverrides_origValues: this.config_fieldOverrides_origValues,
				config: Clone(this.config),
				launchType: this.coreData.launchType,

				variantName: this.coreData.variantName,
				settingValuesSelected: Object.entries(this.coreData.settingValuesSelected).ToMapObj(a=>a[0], a=>a[1].value),
				alarmDelay: this.c.alarms.alarmDelay_lockPerSession ? this.coreData.journey_alarmDelay : undefined,
				alarms_mainSequence: this.c.alarms.phaseAlarm_sequences_lockPerSession ? this.coreData.alarms_currentSequence : null,

				startTime: this.coreData.startTime,
				localOffsetFromUTC: this.localOffsetFromUTC,
				endTime: this.endTime,
				week: moment(this.coreData.startTime).utc().startOf("week").valueOf(),
				events: this.events,
			} as Partial<Session>,
			includeLog && {logEntries: this.logEntries},
		));
		// add _folderName prop, but non enumerably/serializably
		result.VSet("_folderName", this.folderName, {prop: {enumerable: false}});
		return result;
	}
	// this is split into its own function, to allow re-calling through console (by power-users), if issue occurs the first time
	async FinalizeStoredData(saveLocally: boolean, saveOnline: boolean) {
		const sessionInfo = this.GetSessionInfo()!;
		const sessionID_whileLive = this.coreData.id_live;

		if (InDesktop()) {
			if (saveLocally) {
				SaveSessionGeneralData(sessionInfo._folderName, sessionInfo, this.logEntries);
			} else {
				// place session-folder in trash (exists from log-file writing)
				DeleteSessionFolder(sessionInfo._folderName);
			}
			UpdateLocalSessionsMap();
		}

		// add event marking live-session
		if (saveOnline) {
			const sessionID_final = await this.UploadSessionInfo(sessionInfo);
			sessionInfo.id = sessionID_final;
			this.Log(`Session saved online, as id: ${sessionID_final}`, LogType.Event_Large);

			const oldChunks = await gdBelt_rawDataDB.CorrectSessionIDsForChunks(sessionID_whileLive, sessionID_final, true);
			if (oldChunks.length > 0) {
				console.log(`Corrected session-IDs for ${oldChunks.length} gd-belt breath-data chunks. @sessionID_whileLive:${sessionID_whileLive} @sessionID_uploaded:${sessionID_final}`);
			}
			// todo: make sure there are no timing issues with this (eg. of a chunk completing writing a bit slowly, after this code runs -- leaving it stored permanently with the wrong/old session-id)

			// save session-info locally again (since final session-id was obtained)
			if (InDesktop() && saveLocally) {
				SaveSessionGeneralData(sessionInfo._folderName, sessionInfo, this.logEntries);
				UpdateLocalSessionsMap(false, [sessionInfo._folderName!]);
			}
		}
	}
	timelineEvent: TimelineEvent;
	//timelineEventID: string;
	async UploadSessionInfo(sessionInfo: Session) {
		const {id: sessionID} = await new AddSession({entry: sessionInfo}).RunOnServer();

		/*const sessionActivity = (await GetActivities.Async(this.userID)).find(a=>a.name == "FBA session");
		if (sessionActivity != null && addTimelineEvent) {
			this.timelineEvent = new TimelineEvent({
				timeline: this.userID, activity: sessionActivity.id,
				startTime: this.coreData.startTime, endTime: this.endTime,
				sessionID,
			});
			this.timelineEvent.id = (await new AddTimelineEvent({entry: this.timelineEvent}).RunOnServer()).id;
			//await new UpdateTimelineEvent({id: this.timelineEvent.id, updates: {sessionInfo}}).RunOnServer();
			//await new StopTimelineEvent({id: this.timelineEvent.id}).RunOnServer();
			await new StopCurrentTimelineEvents({timelineID: this.userID}).RunOnServer(); // stop all, as cleanup for any left-over from earlier
		}*/

		return sessionID;
	}

	// timers
	initialDelayTimer: Timer;
	autoEndDelayTimer: Timer;

	CreateTimers() {
		const initialDelay_final = this.coreData.launchType == "day" ? weekInMS : this.c.general.initialDelay;
		this.initialDelayTimer = new Timer(initialDelay_final, async()=>{
			this.Log("Initial delay completed.", LogType.Event_Large);
			this.AddEvent({type: "General.InitialDelayEnd"});
			this.Broadcast({}, a=>a.OnInitialDelayCompleted);
		}, 1).SetContext(this.timerContext);

		let autoEndDelayInMS: number;
		if (this.c.general.autoEnd_type == AutoEndType.AtTimeOfDay) {
			// the auto-end-time value includes the date (of when the field's value was set by user); replace that date with the next one that makes the marked time-of-day be in the future 
			const endTime = ChangeTimestampDateToBeAfterNow(this.c.general.autoEnd_time);
			autoEndDelayInMS = endTime - Date.now();
		} else if (this.c.general.autoEnd_type == AutoEndType.AfterDuration) {
			autoEndDelayInMS = this.c.general.autoEnd_delay;
		}

		this.autoEndDelayTimer = new Timer(autoEndDelayInMS!, ()=>{
			this.Log("Auto-end duration completed, thus stopping the session.", LogType.Event_Large);
			Assert(liveSession == this, "Auto-end timer triggered, but the session it triggered for was not the live session!");
			ClearAndStopLiveSession(true, true);
		}, 1).SetContext(this.timerContext);
	}

	ModifyLiveAutoEndTime(newEndTime: number) {
		Assert(this.autoEndDelayTimer.Enabled, "Cannot modify live-session's auto-end time, since the auto-end timer is not enabled!");
		this.autoEndDelayTimer.intervalInMS = newEndTime - Date.now();
		this.autoEndDelayTimer.Start(); // restart with new timer-interval (in this case, really a one-time delay rather than an "interval")
	}

	// events
	// ==========

	// events stored in FBASession_Local, because events are only for record-keeping purposes, thus not useful for remote sessions
	protected events = [] as SessionEvent[];
	get EventsCopy() { return this.events.slice(); }
	AddEvent(eventData: Partial<SessionEvent>) {
		const event = new SessionEvent(eventData);
		this.events.push(event);
		RunInAction("AddEvent", ()=>this.coreData.events.push(event));
	}
	DeleteEventRange(startI: number) {
		this.events.splice(startI);
		RunInAction("DeleteEventRange", ()=>this.coreData.events.splice(startI));
	}

	GetSleepCycleInfo() {
		return GetSleepCycleInfoFromEvents(this.events);
	}
}

export function GetSleepCycleInfoFromEvents(events: SessionEvent[]) {
	// Why not just count the "CycleStart" events? Because journey-cycles are different from sleep-cycles.
	// Sleep-cycles are not 100% defined yet, but each *should* start at a moment roughly shared between session and DJ entry:
	// * Time of session's "ResleepEnd" event.
	// * Time of wake-segment start-time.
	// (almost same moment: drift-phase end triggers wake-segment creation soon after, through "Mark awareness after drifting-end" feature)
	const sleepCycleEnders = events.filter(a=>a.type == "General.SleepCycleEnd");
	const cycleNumber = 1 + sleepCycleEnders.length; // session begins "in cycle 1", not 0
	const lastCycleEnderEvent = sleepCycleEnders.LastOrX();
	const lastCycleEnderEventIndex = events.indexOf(lastCycleEnderEvent as any);
	const eventsThisCycle = lastCycleEnderEventIndex != -1
		? events.slice(lastCycleEnderEventIndex + 1)
		: events;
	return {cycleNumber, lastCycleEnderEvent, lastCycleEnderEventIndex, eventsThisCycle};
}